import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import styles from './Account.module.css';
import Toast from '../../Shared/Toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScroll, faBolt } from '@fortawesome/free-solid-svg-icons';

type BattleLogMode = 'new2d' | 'scrolling' | 'instant';
type BattleViewSpeed = 0.5 | 1 | 2 | 4;

const SPEED_OPTIONS: { value: BattleViewSpeed; label: string }[] = [
  { value: 0.5, label: '0.5× Slow' },
  { value: 1,   label: '1× Normal' },
  { value: 2,   label: '2× Fast' },
  { value: 4,   label: '4× Very Fast' },
];

const BATTLE_LOG_OPTIONS: { value: BattleLogMode; label: string; description: string }[] = [
  {
    value: 'new2d',
    label: '2D Battle Viewer',
    description:
      'Watch the fight play out as an animated 2D arena. HP and XP are not updated until the viewer finishes.',
  },
  {
    value: 'scrolling',
    label: 'Scrolling Text',
    description:
      'Events appear one at a time with round separators, like a live commentary feed. HP and XP are not updated until the full log has printed.',
  },
  {
    value: 'instant',
    label: 'Instant (All at Once)',
    description: 'The full battle log is shown immediately. HP and XP update right away.',
  },
];

const GameSettings: React.FC = () => {
  const [battleLogMode, setBattleLogMode] = useState<BattleLogMode>('new2d');
  const [battleViewSpeed, setBattleViewSpeed] = useState<BattleViewSpeed>(1);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    axios
      .get('/api/user/settings', { withCredentials: true })
      .then(res => {
        if (res.data.battleLogMode) {
          setBattleLogMode(res.data.battleLogMode as BattleLogMode);
        }
        if (res.data.battleViewSpeed) {
          setBattleViewSpeed(res.data.battleViewSpeed as BattleViewSpeed);
        }
      })
      .catch(error => {
        logError(error, 'GameSettings.fetch');
      });
  }, []);

  const handleSaveBattleLogMode = async (mode: BattleLogMode) => {
    try {
      setBattleLogMode(mode);
      await axios.put('/api/user/settings', { battleLogMode: mode }, { withCredentials: true });
      setToastMessage('Battle log mode updated.');
      setToastType('success');
    } catch (error) {
      logError(error, 'GameSettings.saveBattleLogMode');
      setToastMessage(getErrorMessage(error, 'Failed to save setting.'));
      setToastType('error');
    }
  };

  const handleSaveBattleViewSpeed = async (speed: BattleViewSpeed) => {
    try {
      setBattleViewSpeed(speed);
      await axios.put('/api/user/settings', { battleViewSpeed: speed }, { withCredentials: true });
      setToastMessage('Playback speed updated.');
      setToastType('success');
    } catch (error) {
      logError(error, 'GameSettings.saveBattleViewSpeed');
      setToastMessage(getErrorMessage(error, 'Failed to save setting.'));
      setToastType('error');
    }
  };

  return (
    <div className={styles.accountContainer}>
      <h1>Game Settings</h1>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      <div className={styles.settingsSection}>
        <h2>
          <FontAwesomeIcon icon={faScroll} /> Battle Log Mode
        </h2>
        <p className={styles.settingsSectionDescription}>
          Choose how battle results are displayed after a fight.
        </p>
        <div className={styles.battleLogOptions}>
          {BATTLE_LOG_OPTIONS.map(option => (
            <button
              key={option.value}
              className={`${styles.battleLogOption} ${
                battleLogMode === option.value ? styles.battleLogOptionActive : ''
              }`}
              onClick={() => handleSaveBattleLogMode(option.value)}
            >
              <span className={styles.battleLogOptionLabel}>{option.label}</span>
              <span className={styles.battleLogOptionDesc}>{option.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h2>
          <FontAwesomeIcon icon={faBolt} /> 2D Viewer Playback Speed
        </h2>
        <p className={styles.settingsSectionDescription}>
          Default playback speed for the 2D Battle Viewer. Can also be changed mid-battle.
        </p>
        <div className={styles.battleLogOptions}>
          {SPEED_OPTIONS.map(option => (
            <button
              key={option.value}
              className={`${styles.battleLogOption} ${
                battleViewSpeed === option.value ? styles.battleLogOptionActive : ''
              }`}
              onClick={() => handleSaveBattleViewSpeed(option.value)}
            >
              <span className={styles.battleLogOptionLabel}>{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameSettings;
